import React, { useEffect, useState } from "react"
import { useDispatch, useSelector } from "react-redux";
import { StyledModalBody } from "../../../../components/Modals";
import { ButtonBodyVariant } from "../../../../components/ui/ButtonVariant";
import { useTranslation } from "react-i18next";
import { closeActiveModal, openModal } from "../../../../helper/modals";
import { Flex } from "../../../../components/ui/Flex";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "../../../../components/ui/Form";
import { useForm, useWatch } from "react-hook-form";
import { Select, SelectItem } from "../../../../components/ui/Select";
import { LoadingSpinner, ModalButtonsContainer, PaymentModal, TitleText } from "../../PaymentComponents";
import * as Yup from 'yup';
import { yupResolver } from "@hookform/resolvers/yup";
import { getVopayUser, resetPaymentUserData, updatePaymentData } from "../../../../redux/actions/payments";
import UserAsyncDropDown from "../../../../components/UserAsyncDropDown";
import { requestPending, requestSuccess } from "../../../../utils/status";
import { GET_ALL_BUILDINGS, GET_VOPAY_USER } from "../../../../redux/constants";
import { setToast } from "../../../../redux/actions/window";
import { Fonts, Palette } from "../../../../components/utils";
import { IoCheckmarkCircle } from "react-icons/io5";
import styled from "styled-components";
import { RiErrorWarningFill } from "react-icons/ri";
import {
  fetchBuildingVopayOnboardingStatus,
  isBuildingVopayReadyForPayments
} from "../../PaymentUtils";

const schema = Yup.object().shape({
  selectedBuilding: Yup.mixed()
    .required('A building selection is required'),
  userId: Yup.object()
    .test('userId', 'A user selection is required', (user) => {
      return user?.value > 0;
    })
    .required('A user selection is required')
});

const CreatePaymentModal = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();

  const modal = useSelector((state) => state.headerModal.modal);
  const addPayment = useSelector((state) => state.addPayment);

  const allBuildings = useSelector((state) => state.building.allBuildings);
  const allBuildingsStatus = useSelector((state) => state.building.allBuildingsStatus);

  const [buildingOnboardingStatus, setBuildingOnboardingStatus] = useState(undefined);
  const [buildingOnboardingLoading, setBuildingOnboardingLoading] = useState(false);

  const form = useForm({
    resolver: yupResolver(schema),
    mode: 'onChange',
    defaultValues: {
      selectedBuilding: addPayment.selectedBuilding,
      userId: addPayment.userId,
    }
  });

  const watchBuilding = useWatch({
    control: form.control,
    name: 'selectedBuilding'
  });

  const watchUser = useWatch({
    control: form.control,
    name: 'userId'
  });

  const buildingVopayEnabled = !!watchBuilding?.config?.enable_vopay;
  const buildingReady =
    buildingVopayEnabled && isBuildingVopayReadyForPayments(buildingOnboardingStatus);
  const residentStepsDisabled = !buildingReady || buildingOnboardingLoading;

  const shouldDisableSubmitButton =
    watchBuilding === undefined ||
    watchUser === undefined ||
    !form.formState.isValid ||
    residentStepsDisabled ||
    addPayment.userVopayAccounStatus === requestPending(GET_VOPAY_USER);
  const foundSelectedUser =
    buildingReady &&
    watchUser !== undefined &&
    addPayment.userVopayAccounStatus === requestSuccess(GET_VOPAY_USER);

  const vopayUser = addPayment.userVopayAccountData
  const hasVopayAccount = vopayUser != null && vopayUser.vopayAccountId != null
  const hasDefaultBank = hasVopayAccount && vopayUser?.hasDefaultBank === true

  const onSubmit = (values) => {
    if (!buildingReady) {
      return;
    }

    dispatch(updatePaymentData(values))

    if (hasVopayAccount && hasDefaultBank) {
      openModal('SelectPaymentTypeModal')
    } else if (hasVopayAccount && !hasDefaultBank) {
      openModal('AccountNotSetupModal', { paymentMethodSetupOnly: true })
    } else {
      openModal('AccountNotSetupModal')
    }
  }

  useEffect(() => {
    if (watchBuilding?.id !== addPayment.selectedBuilding?.id) {
      form.setValue('userId', null);
    }
  }, [watchBuilding, addPayment.selectedBuilding, form]);

  useEffect(() => {
    const buildingId = watchBuilding?.id;
    if (!buildingId || modal !== 'CreatePaymentModal') {
      setBuildingOnboardingStatus(undefined);
      setBuildingOnboardingLoading(false);
      return;
    }

    if (!watchBuilding?.config?.enable_vopay) {
      setBuildingOnboardingStatus(undefined);
      setBuildingOnboardingLoading(false);
      return;
    }

    let cancelled = false;
    setBuildingOnboardingLoading(true);
    setBuildingOnboardingStatus(undefined);

    fetchBuildingVopayOnboardingStatus(buildingId)
      .then((data) => {
        if (cancelled) return;
        setBuildingOnboardingStatus(data?.status);
      })
      .catch(() => {
        if (cancelled) return;
        setBuildingOnboardingStatus(undefined);
        dispatch(
          setToast({
            toastShow: true,
            toastMessage: t('Failed to get building onboarding status!'),
            modal: 'errorToast'
          })
        );
      })
      .finally(() => {
        if (!cancelled) {
          setBuildingOnboardingLoading(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [watchBuilding?.id, watchBuilding?.config?.enable_vopay, modal, dispatch, t]);

  useEffect(() => {
    if (!buildingReady && watchUser != null) {
      form.setValue('userId', null);
    }
  }, [buildingReady, watchUser, form]);

  useEffect(() => {
    if (watchUser?.value && watchBuilding?.id && buildingReady) {
      dispatch(getVopayUser({
        buildingID: watchBuilding?.id,
        userId: watchUser.value,
        fail: () =>
          dispatch(
            setToast({
              toastShow: true,
              toastMessage: t('Failed to get user!'),
              modal: 'errorToast'
            })
          )
      }));
    }
  }, [watchUser, watchBuilding?.id, buildingReady, dispatch, t]);

  useEffect(() => {
    if (!watchUser?.value || !buildingReady) {
      dispatch(resetPaymentUserData())
    }
  }, [watchUser, buildingReady, dispatch]);

  const showBuildingStatusRow = !!watchBuilding?.id;

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)}>
        <PaymentModal
          show={modal === 'CreatePaymentModal'}
          onClose={closeActiveModal}
          closeOnBackdrop={false}
          width={'460px'}>
          <StyledModalBody>
            <Flex direction="column" gap="1rem">
              <TitleText>Create Payment</TitleText>

              <Flex direction="column" alignItems="center" gap="1rem">
                <FormField
                  control={form.control}
                  name="selectedBuilding"
                  render={({ field }) => {
                    return (
                      <FormItem alt>
                        <FormLabel v3>{t('Select building')}</FormLabel>
                        <FormControl>
                          <Select
                            {...field}
                            getPopupContainer={() => document.body}
                            onChange={(value) => {
                              const foundBuilding = allBuildings.find((building) => building.id === value)
                              field.onChange(foundBuilding)
                            }}
                            value={field.value?.id}
                            loading={allBuildingsStatus === requestPending(GET_ALL_BUILDINGS)}
                            placeholder={'Please select'}>
                            {allBuildings?.length > 0 ? (
                              <SelectItem value={-1}>{t('Please select')}</SelectItem>
                            ) : (
                              <SelectItem value={-1}>{t('No Buildings Available')}</SelectItem>
                            )}

                            {allBuildings.map((building) => (
                              <SelectItem
                                key={building.id}
                                value={building.id}
                                disabled={!building.config?.enable_vopay}
                              >
                                {building.name}
                              </SelectItem>
                            ))}
                          </Select>
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )
                  }}
                />

                {showBuildingStatusRow && !buildingReady && (
                  <Flex gap="0.5rem" alignItems="center" style={{ width: "100%" }}>
                    {buildingOnboardingLoading ? (
                      <LoadingSpinner size={18} />
                    ) : (
                      <RiErrorWarningFill size={24} color={Palette.ORANGE_500} />
                    )}
                    <VopayAccountConfirmationText>
                      {t('VoPay onboarding is not complete')}
                    </VopayAccountConfirmationText>
                  </Flex>
                )}

                <FormField
                  control={form.control}
                  name="userId"
                  render={({ field }) => (
                    <FormItem alt>
                      <FormLabel v3>{t('Select Resident')}</FormLabel>
                      <FormControl>
                        <UserAsyncDropDown
                          {...field}
                          onChange={(e) => {
                            field.onChange(e);
                          }}
                          noUser={true}
                          skip_deleted={true}
                          type={1}
                          only_residents
                          placeholder={t('Select Resident')}
                          buildingId={watchBuilding?.id}
                          disabled={residentStepsDisabled}
                          menuPortalTarget={document.body}
                          menuPosition={'fixed'}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                {foundSelectedUser &&
                  <Flex gap="0.5rem" alignItems="center" style={{ width: "100%" }}>
                    {!hasVopayAccount ? (
                      <>
                        <RiErrorWarningFill size={24} color={Palette.ORANGE_500} />
                        <VopayAccountConfirmationText>{t('No Account Found')}</VopayAccountConfirmationText>
                      </>
                    ) : !hasDefaultBank ? (
                      <>
                        <RiErrorWarningFill size={24} color={Palette.HAZY_BLUE}/>
                        <VopayAccountConfirmationText>{t('No Payment Method Setup')}</VopayAccountConfirmationText>
                      </>
                    ) : (
                      <>
                        <IoCheckmarkCircle size={24} color={Palette.GREEN_500} />
                        <VopayAccountConfirmationText>{t('Success')}</VopayAccountConfirmationText>
                      </>
                    )}
                  </Flex>
                }

              </Flex>
              <ModalButtonsContainer gapY="8px">
                <ButtonBodyVariant cssOverride="width: 8.875rem; height: 2rem;" type="button" variant="secondary" onClick={closeActiveModal}>{t('Cancel')}</ButtonBodyVariant>
                <ButtonBodyVariant cssOverride="width: 8.875rem; height: 2rem; position: relative;" type="submit" disabled={shouldDisableSubmitButton}>
                  {t('Next')}
                  {(buildingOnboardingLoading ||
                    addPayment.userVopayAccounStatus === requestPending(GET_VOPAY_USER)) && (
                    <LoadingSpinner size={18} style={{ position: "absolute", right: "1.25rem" }} />
                  )}
                </ButtonBodyVariant>
              </ModalButtonsContainer>
            </Flex>
          </StyledModalBody>
        </PaymentModal>
      </form>
    </Form >
  )
}

const VopayAccountConfirmationText = styled.p`
  color: ${Palette.BLACK};
  font-family: ${Fonts.OPEN_SANS_FONT};
  font-size: 0.75rem;
  font-style: normal;
  font-weight: 400;
  line-height: 1.5rem;
  letter-spacing: 0rem;
  margin: 0;
`

export default CreatePaymentModal
